import { createReactElement, UpdatableReactElement } from '../../utils/react.utils'
import { BlockEventSubscriptionType, BlockStyleProps, ITaskBlock } from './ITaskBlock'
import { withBlockContext } from './BlockStateContext'
import { BlockComponentProps } from '../core/types'
import { withTags } from '../components/TagsHOC/TagsHOC'
import { ComponentType } from 'react'
import { signal, Signal, batch } from '@preact/signals-react'
import { TagItemsProps, TagItemType } from '../components/TagItems/TagItems'
import { Subscription } from '../../core/Subscription'

export type BlockConfig<T = object> = {
    type: string
    id: string
    name: string
    component: ComponentType<BlockComponentProps<T> & BlockStyleProps>
    defaultProps?: T
    componentProps?: T & BlockStyleProps
}

export type BlockRefType = React.RefObject<{ getContainer: () => HTMLElement | null }>

export class TaskBlock<T extends BlockStyleProps> implements ITaskBlock {
    type: string
    name: string
    id: string
    subscription: Subscription<BlockEventSubscriptionType, never> = new Subscription()
    private element: UpdatableReactElement
    private props?: T
    private isVisible = true
    private state: Record<string, Signal<unknown>> = {}
    private listeners: Record<string, Signal<((...args: unknown[]) => unknown) | undefined>> = {}
    private methods: Record<string, (...args: unknown[]) => unknown> = {}
    private pendingPostMessages: unknown[] = []
    private tags: Signal<TagItemsProps['items']> = signal({})
    private ref?: BlockRefType

    constructor(config: BlockConfig<T>) {
        this.type = config.type
        this.id = config.id
        this.name = config.name
        this.initializeDefaultMethods()
        const componentDefinition = withBlockContext(withTags(config.component, this.tags), {
            getIsVisible: () => this.isVisible,
            getBlockState: this.getState.bind(this),
            setBlockState: this.setState.bind(this),
            getBlockListener: this.getListener.bind(this),
            setBlockListener: this.setListener.bind(this),
            setMethod: this.setMethod.bind(this),
            cleanup: this.clear.bind(this),
            setRef: this.setRef.bind(this),
            blockId: this.id,
        })
        this.props = config.componentProps || config.defaultProps
        this.element = createReactElement(
            componentDefinition as React.FC<T>,
            this.props as T & BlockStyleProps
        )
    }

    private initializeDefaultMethods(): void {
        this.setMethod('show', this.show.bind(this))
        this.setMethod('hide', this.hide.bind(this))
        this.setMethod('setProperty', (...args: unknown[]) => {
            const newProps = args[0] as Partial<BlockComponentProps<unknown> & BlockStyleProps>
            this.updateProps(newProps)
        })
        this.setMethod('getId', () => this.id)
        this.setMethod('getName', () => this.name)
    }

    getElement() {
        return this.element
    }

    show() {
        this.isVisible = true
        this.render()
    }

    hide() {
        this.isVisible = false
        this.render()
    }

    render() {
        this.element?.updater.forceUpdate()
    }

    updateProps(newProps: Partial<BlockComponentProps<unknown> & BlockStyleProps>) {
        Object.assign(this.props || {}, newProps)
        if ('visibility' in this.props! && this.props.visibility === 'hidden') {
            this.isVisible = false
        } else {
            this.isVisible = true
        }
        this.render()
    }

    getState(key: string): Signal<unknown> {
        if (!this.state[key]) {
            const newSignal = signal<unknown>(null)
            this.state[key] = newSignal
        }
        return this.state[key]
    }

    setState(key: string, value: unknown): void {
        const state = this.getState(key)
        batch(() => {
            state.value = value
        })
    }

    getListener(key: string): Signal<((...args: unknown[]) => unknown) | undefined> {
        if (!this.listeners[key]) {
            const newSignal = signal<((...args: unknown[]) => unknown) | undefined>(undefined)
            this.listeners[key] = newSignal
        }
        return this.listeners[key]
    }

    setListener(key: string, listener: ((...args: unknown[]) => unknown) | undefined): void {
        const listenerSignal = this.getListener(key)
        batch(() => {
            listenerSignal.value = listener
        })
    }

    getListeners(): Record<string, Signal<((...args: unknown[]) => unknown) | undefined>> {
        return this.listeners
    }

    private setMethod(key: string, method: (...args: unknown[]) => unknown): void {
        this.methods[key] = method
        if (key === 'postMessage' && this.pendingPostMessages.length > 0) {
            const pending = [...this.pendingPostMessages]
            this.pendingPostMessages = []
            pending.forEach(message => method(message))
        }
    }

    private getMethod(key: string): ((...args: unknown[]) => unknown) | undefined {
        return this.methods[key]
    }

    executeMethod(key: string, ...args: unknown[]): unknown {
        const method = this.getMethod(key)
        if (!method) {
            if (key === 'postMessage' && args[0] !== undefined) {
                this.pendingPostMessages.push(args[0])
            }
            return undefined
        }
        return method(...args)
    }

    setTags(tagId: string, item: TagItemType | undefined): void {
        batch(() => {
            this.tags.value = {
                ...this.tags.value,
                [tagId]: item,
            }
        })
    }

    getTags(): TagItemsProps['items'] {
        return this.tags.value
    }

    private setRef(ref: BlockRefType): void {
        this.ref = ref
        this.subscription.publishUpdate(BlockEventSubscriptionType.RefAdded)
    }

    getRefContainer(): HTMLElement | null {
        return this.ref?.current?.getContainer() ?? null
    }

    getProps(): T | undefined {
        return this.props
    }

    clear = () => {
        Object.values<Signal<unknown>>(this.state).forEach((signal: Signal<unknown>) => {
            signal.value = null
        })
        Object.values<Signal<((...args: unknown[]) => unknown) | undefined>>(
            this.listeners
        ).forEach(signal => {
            signal.value = undefined
        })
        this.state = {}
        this.listeners = {}
        this.methods = {}
        this.pendingPostMessages = []
    }
}
